import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import { navLinks, companyInfo, serviceDropdown } from '../../data/mock';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled
        ? 'bg-slate-950/95 backdrop-blur-lg border-b border-slate-800/50'
        : 'bg-transparent'
        }`}
    >
      <nav className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2 group"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            <img
              src="/echo-media-logo-transparent.png"
              alt="Echo Media Logo"
              className="h-16 w-auto object-contain transition-all duration-300 group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => {
              if (link.name === 'Services') {
                return (
                  <div key={link.name} className="relative group px-2">
                    <Link
                      to={link.path}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 inline-flex items-center gap-1 ${isActive(link.path)
                        ? 'text-emerald-400 bg-emerald-400/10'
                        : 'text-slate-300 group-hover:text-white group-hover:bg-slate-800/50'
                        }`}
                    >
                      {link.name}
                      <ChevronRight size={14} className="group-hover:rotate-90 transition-transform duration-200" />
                    </Link>

                    {/* Mega Menu Dropdown */}
                    <div className="absolute top-full left-1/2 -translate-x-1/2 w-[900px] mt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 transform origin-top -translate-y-2 group-hover:translate-y-0 z-50">
                      <div className="bg-slate-950/95 backdrop-blur-xl border border-slate-800/50 rounded-2xl shadow-2xl p-8 overflow-hidden">
                        <div className="grid grid-cols-4 gap-8">
                          {serviceDropdown.map((category, idx) => (
                            <div key={idx} className="space-y-4">
                              <h3 className="text-emerald-400 text-xs font-bold uppercase tracking-wider border-b border-slate-800 pb-2">
                                {category.title}
                              </h3>
                              <ul className="space-y-2">
                                {category.items.length > 0 ? (
                                  category.items.map((item, itemIdx) => (
                                    <li key={itemIdx}>
                                      <Link
                                        to="/services"
                                        className="text-slate-400 hover:text-white text-sm transition-colors block py-0.5 hover:translate-x-1 duration-200"
                                      >
                                        {item}
                                      </Link>
                                    </li>
                                  ))
                                ) : (
                                  <li>
                                    <span className="text-slate-600 text-sm italic">Coming Soon</span>
                                  </li>
                                )}
                              </ul>
                            </div>
                          ))}
                        </div>

                        {/* Featured Service / Promotion Area */}
                        <div className="mt-8 pt-6 border-t border-slate-800/50 flex items-center justify-between">
                          <div>
                            <h4 className="text-white font-semibold mb-1">Passionate about Quality?</h4>
                            <p className="text-slate-400 text-xs">Explore how our services can transform your business.</p>
                          </div>
                          <Link to="/services">
                            <Button size="sm" variant="outline" className="text-xs border-slate-700 hover:bg-slate-800 hover:text-white">
                              View All Services
                            </Button>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${isActive(link.path)
                    ? 'text-emerald-400 bg-emerald-400/10'
                    : 'text-slate-300 hover:text-white hover:bg-slate-800/50'
                    }`}
                >
                  {link.name}
                </Link>
              );
            })}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <Link to="/contact">
              <Button className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold shadow-lg shadow-emerald-500/20 hover:shadow-emerald-500/40 transition-all duration-300 rounded-full px-6">
                Let's Build Together
                <ChevronRight size={16} className="ml-1" />
              </Button>
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            className="lg:hidden p-2 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800/50 transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-slate-800/50 animate-in slide-in-from-top-2 duration-200 h-[calc(100vh-80px)] overflow-y-auto">
            <div className="flex flex-col gap-2 pb-8">
              {navLinks.map((link) => (
                <div key={link.name}>
                  <Link
                    to={link.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${isActive(link.path)
                      ? 'text-emerald-400 bg-emerald-400/10'
                      : 'text-slate-300 hover:text-white hover:bg-slate-800/50'
                      }`}
                  >
                    {link.name}
                  </Link>
                  {/* Mobile Submenu for Services */}
                  {link.name === 'Services' && (
                    <div className="pl-8 pr-4 py-2 space-y-4 border-l border-slate-800 ml-4 mt-2 mb-2">
                      {serviceDropdown.map((category, idx) => (
                        <div key={idx} className="space-y-2">
                          <h4 className="text-emerald-500/80 text-xs font-bold uppercase">{category.title}</h4>
                          <ul className="space-y-1">
                            {category.items.slice(0, 5).map((item, itemIdx) => (
                              <li key={itemIdx} className="text-slate-400 text-sm py-1">{item}</li>
                            ))}
                            {category.items.length > 5 && (
                              <li className="text-slate-500 text-xs italic">and more...</li>
                            )}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <div className="px-4 pt-4">
                <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold rounded-full">
                    Let's Build Together
                    <ChevronRight size={16} className="ml-1" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export { Header };
